/**
 * AnnotationsContext
 *
 * Drawing state for the viewer PLUS persistence for annotation decisions
 * (ported from rc-app's accept/reject semantics, 2026-09-14):
 *
 *   persistDrawnAnnotation(box, belongs) -> POST media_class
 *     { media_id, class_id, bbox…, inferred:false, belongs, score:null,
 *       created_by: organizationId, owner_account: <cognito sub> }
 *   resolveSuggestion(id, belongs)       -> PATCH media_class/{id}
 *     { media_id, skill_id, inferred:false, belongs, owner_account: <sub> }
 *
 * Both invalidate the box-overlay + ground-truth queries on success and record
 * a single-level `lastAction` whose `undo()` issues the inverse mutation
 * (delete for creates; PATCH back to inferred for suggestion resolutions).
 * The legacy local-only add/update/delete methods remain for draw preview.
 */

import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react'
import type { MediaClass } from '@/types/api'
import type { AnnotationMode, AnnotationTool, NormalizedBox, PixelBox } from '@/types/annotations'
import type { BoxAnnotation } from '@server/routers/annotations'
import { useAuth } from '@/contexts/AuthContext'
import { trpc } from '@/lib/trpc'

export interface LastAnnotationAction {
  label: string
  undo: () => Promise<void>
}

interface AnnotationsContextType {
  // State
  annotations: MediaClass[]
  mode: AnnotationMode
  tool: AnnotationTool
  selectedAnnotationId: string | null

  // Actions
  setAnnotations: (annotations: MediaClass[]) => void
  setMode: (mode: AnnotationMode) => void
  setTool: (tool: AnnotationTool) => void
  setSelectedAnnotationId: (id: string | null) => void
  addAnnotation: (box: NormalizedBox) => void
  updateAnnotation: (id: string, box: NormalizedBox) => void
  deleteAnnotation: (id: string) => void
  confirmAnnotation: (id: string) => void
  rejectAnnotation: (id: string) => void

  // Persistence (rc-app accept/reject parity)
  persistDrawnAnnotation: (box: NormalizedBox, belongs: boolean) => Promise<void>
  resolveSuggestion: (
    suggestion: BoxAnnotation,
    belongs: boolean,
    /** rc-app parity: a resized box forwarded from the editor on approve. */
    adjustedBbox?: NormalizedBox,
  ) => Promise<void>
  lastAction: LastAnnotationAction | null
  undoLastAction: () => Promise<void>
  dismissLastAction: () => void

  // Utility
  normalizeBox: (pixelBox: PixelBox, imageWidth: number, imageHeight: number) => NormalizedBox
  denormalizeBox: (normalizedBox: NormalizedBox, imageWidth: number, imageHeight: number) => PixelBox
}

const AnnotationsContext = createContext<AnnotationsContextType | undefined>(undefined)

interface AnnotationsProviderProps {
  children: ReactNode
  mediaId: string
  classId: string
  /** owner org of the skill — rc-app stores this as created_by on drawn boxes */
  organizationId?: string
  /** needed in the PATCH body for suggestion resolutions (rc-app parity) */
  skillId?: string
  initialAnnotations?: MediaClass[]
}

export function AnnotationsProvider({
  children,
  mediaId,
  classId,
  organizationId,
  skillId,
  initialAnnotations = [],
}: AnnotationsProviderProps) {
  const [annotations, setAnnotations] = useState<MediaClass[]>(initialAnnotations)
  const [mode, setMode] = useState<AnnotationMode>('view')
  const [tool, setTool] = useState<AnnotationTool>('rectangle')
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null)
  const [lastAction, setLastAction] = useState<LastAnnotationAction | null>(null)

  const auth = useAuth()
  const cognitoSub = auth?.user?.id
  const utils = trpc.useUtils()
  const createMutation = trpc.mediaClasses.create.useMutation()
  const updateMutation = trpc.mediaClasses.update.useMutation()
  const deleteMutation = trpc.mediaClasses.delete.useMutation()

  const invalidateBoxQueries = useCallback(async () => {
    await Promise.all([
      utils.annotations.getBoxesByMedia.invalidate({ mediaId }),
      skillId ? utils.mediaClasses.getGroundTruth.invalidate({ skillId }) : Promise.resolve(),
    ])
  }, [utils, mediaId, skillId])

  // Persist a freshly drawn box as ground truth (belongs=true) or as a negative
  // example (belongs=false) — rc-app's addBox / reject-drawn-box POST payloads.
  const persistDrawnAnnotation = useCallback(
    async (box: NormalizedBox, belongs: boolean) => {
      const created = (await createMutation.mutateAsync({
        media_id: mediaId,
        class_id: classId,
        bbox_x1: box.x1,
        bbox_y1: box.y1,
        bbox_x2: box.x2,
        bbox_y2: box.y2,
        inferred: false,
        belongs,
        score: null,
        created_by: organizationId ?? classId,
        owner_account: cognitoSub ?? null,
      })) as { id?: string } | undefined
      await invalidateBoxQueries()

      const createdId = created?.id
      setLastAction({
        label: belongs ? 'Saved as ground truth' : 'Saved as negative example',
        undo: async () => {
          if (createdId) {
            await deleteMutation.mutateAsync({ id: createdId })
            await invalidateBoxQueries()
          }
        },
      })
    },
    [createMutation, deleteMutation, mediaId, classId, organizationId, cognitoSub, invalidateBoxQueries],
  )

  // Accept/reject an inference suggestion — rc-app's PATCH semantics. Reject
  // keeps the row as a negative; nothing is deleted.
  const resolveSuggestion = useCallback(
    async (suggestion: BoxAnnotation, belongs: boolean, adjustedBbox?: NormalizedBox) => {
      await updateMutation.mutateAsync({
        id: suggestion.id,
        data: {
          media_id: mediaId,
          skill_id: skillId,
          inferred: false,
          belongs,
          owner_account: cognitoSub ?? null,
          // rc-app parity: an approve can carry a resized box
          ...(adjustedBbox
            ? {
                bbox_x1: adjustedBbox.x1,
                bbox_y1: adjustedBbox.y1,
                bbox_x2: adjustedBbox.x2,
                bbox_y2: adjustedBbox.y2,
              }
            : {}),
        },
      })
      await invalidateBoxQueries()

      setLastAction({
        label: belongs
          ? adjustedBbox
            ? 'Suggestion resized & accepted'
            : 'Suggestion accepted'
          : 'Suggestion rejected',
        undo: async () => {
          await updateMutation.mutateAsync({
            id: suggestion.id,
            // Restore the original suggestion state: inference rows are written
            // with belongs=true, and a resize is rolled back to the model's box.
            data: {
              media_id: mediaId,
              skill_id: skillId,
              inferred: true,
              belongs: true,
              ...(adjustedBbox
                ? {
                    bbox_x1: suggestion.bbox.x1,
                    bbox_y1: suggestion.bbox.y1,
                    bbox_x2: suggestion.bbox.x2,
                    bbox_y2: suggestion.bbox.y2,
                  }
                : {}),
            },
          })
          await invalidateBoxQueries()
        },
      })
    },
    [updateMutation, mediaId, skillId, cognitoSub, invalidateBoxQueries],
  )

  const undoLastAction = useCallback(async () => {
    if (!lastAction) return
    const action = lastAction
    setLastAction(null)
    await action.undo()
  }, [lastAction])

  const dismissLastAction = useCallback(() => setLastAction(null), [])

  // Normalize pixel coordinates to 0-1 range
  const normalizeBox = useCallback(
    (pixelBox: PixelBox, imageWidth: number, imageHeight: number): NormalizedBox => {
      return {
        x1: pixelBox.x1 / imageWidth,
        y1: pixelBox.y1 / imageHeight,
        x2: pixelBox.x2 / imageWidth,
        y2: pixelBox.y2 / imageHeight,
      }
    },
    [],
  )

  // Denormalize 0-1 range to pixel coordinates
  const denormalizeBox = useCallback(
    (normalizedBox: NormalizedBox, imageWidth: number, imageHeight: number): PixelBox => {
      return {
        x1: Math.round(normalizedBox.x1 * imageWidth),
        y1: Math.round(normalizedBox.y1 * imageHeight),
        x2: Math.round(normalizedBox.x2 * imageWidth),
        y2: Math.round(normalizedBox.y2 * imageHeight),
      }
    },
    [],
  )

  // Add new annotation
  const addAnnotation = useCallback(
    (box: NormalizedBox) => {
      // TODO: Call API to create annotation
      // For now, create a local annotation
      const newAnnotation: MediaClass = {
        id: `temp-${Date.now()}`,
        media_id: mediaId,
        class_id: classId,
        bbox_x1: box.x1,
        bbox_y1: box.y1,
        bbox_x2: box.x2,
        bbox_y2: box.y2,
        belongs: false, // Not confirmed yet
        inferred: false,
        created_by: 'user', // TODO: Get from session
        created_at: new Date().toISOString(),
      }

      setAnnotations((prev) => [...prev, newAnnotation])
    },
    [mediaId, classId],
  )

  // Update existing annotation
  const updateAnnotation = useCallback((id: string, box: NormalizedBox) => {
    // TODO: Call API to update annotation
    setAnnotations((prev) =>
      prev.map((ann) =>
        ann.id === id
          ? {
              ...ann,
              bbox_x1: box.x1,
              bbox_y1: box.y1,
              bbox_x2: box.x2,
              bbox_y2: box.y2,
            }
          : ann,
      ),
    )
  }, [])

  // Delete annotation
  const deleteAnnotation = useCallback((id: string) => {
    // TODO: Call API to delete annotation
    setAnnotations((prev) => prev.filter((ann) => ann.id !== id))
  }, [])

  // Confirm annotation (approve)
  const confirmAnnotation = useCallback((id: string) => {
    // TODO: Call API to confirm annotation
    setAnnotations((prev) =>
      prev.map((ann) =>
        ann.id === id
          ? {
              ...ann,
              belongs: true,
              inferred: false,
            }
          : ann,
      ),
    )
  }, [])

  // Reject annotation
  const rejectAnnotation = useCallback((id: string) => {
    // TODO: Call API to reject annotation
    setAnnotations((prev) =>
      prev.map((ann) =>
        ann.id === id
          ? {
              ...ann,
              belongs: false,
              inferred: false,
            }
          : ann,
      ),
    )
  }, [])

  const value: AnnotationsContextType = useMemo(
    () => ({
      annotations,
      mode,
      tool,
      selectedAnnotationId,
      setAnnotations,
      setMode,
      setTool,
      setSelectedAnnotationId,
      addAnnotation,
      updateAnnotation,
      deleteAnnotation,
      confirmAnnotation,
      rejectAnnotation,
      persistDrawnAnnotation,
      resolveSuggestion,
      lastAction,
      undoLastAction,
      dismissLastAction,
      normalizeBox,
      denormalizeBox,
    }),
    [
      annotations,
      mode,
      tool,
      selectedAnnotationId,
      setAnnotations,
      setMode,
      setTool,
      setSelectedAnnotationId,
      addAnnotation,
      updateAnnotation,
      deleteAnnotation,
      confirmAnnotation,
      rejectAnnotation,
      persistDrawnAnnotation,
      resolveSuggestion,
      lastAction,
      undoLastAction,
      dismissLastAction,
      normalizeBox,
      denormalizeBox,
    ],
  )

  return <AnnotationsContext.Provider value={value}>{children}</AnnotationsContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAnnotations() {
  const context = useContext(AnnotationsContext)
  if (context === undefined) {
    // Return null instead of throwing to allow usage outside provider
    return null
  }
  return context
}
